import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';

import { createApiClient } from '@/api/client';
import type { ApiError, LegacyKuduResponseEnvelope } from '@/api/client.types';
import { createLegacyKuduToken, getKuduUserId } from '@/api/legacyKuduAuth';
import { useAuth } from '@/auth/useAuth';
import { studentGradesQueryKeyPrefix } from '@/features/gradebook/useStudentGrades';
import type { ComponentPageData } from '@/features/content/useComponent';

import { statsFailure } from './statsFailure';

interface ZeroedQuestionStatPayload {
  answer: {
    answer_index: number[];
  };
  score: number;
}

interface ZeroedSectionStatsPayload {
  id: string;
  pre_lecture_question_stats?: ZeroedQuestionStatPayload[];
  question_stats: ZeroedQuestionStatPayload[];
  total_pre_lecture_score?: number;
  total_score: number;
}

export interface UpdateStatsRequestPayload {
  component: {
    section_stats: ZeroedSectionStatsPayload[];
  };
  component_id: string;
  course_id: string;
  grade_component: boolean;
  is_instructor_request: boolean;
  only_score_pre_lecture: boolean;
  user: NonNullable<ComponentPageData['instructorUser']>;
  user_id: string;
}

export interface UpdateStatsInput {
  component: ComponentPageData;
  componentId: string;
  courseId: string;
  // When true, build the Kai Coach payload (no question sections — the backend
  // aggregates per-unit participation stats from the stored student components).
  coach?: boolean;
}

function createDataError(message: string): ApiError {
  const error = new Error(message) as ApiError;
  error.kind = 'data';
  return error;
}

function createZeroedQuestionStat(): ZeroedQuestionStatPayload {
  return {
    answer: {
      answer_index: [-1],
    },
    score: 0,
  };
}

export function buildUpdateStatsPayload(
  component: ComponentPageData,
  courseId: string,
  componentId: string,
  userId: string,
): UpdateStatsRequestPayload {
  if (!component.instructorUser) {
    throw createDataError(
      'Instructor stats updates require the instructor User object from the component query.',
    );
  }

  if (component.instructorStatsSections.length === 0) {
    throw createDataError(
      'Instructor stats updates require normalized section stats metadata.',
    );
  }

  return {
    component: {
      section_stats: component.instructorStatsSections.map((section) => ({
        id: section.sectionStatsId ?? section.sectionId,
        ...(section.preLectureQuestionCount > 0
          ? {
              pre_lecture_question_stats: Array.from(
                { length: section.preLectureQuestionCount },
                () => createZeroedQuestionStat(),
              ),
              total_pre_lecture_score: 0,
            }
          : {}),
        question_stats: Array.from(
          { length: section.homeworkQuestionCount },
          () => createZeroedQuestionStat(),
        ),
        total_score: 0,
      })),
    },
    component_id: componentId,
    course_id: courseId,
    grade_component: false,
    is_instructor_request: true,
    only_score_pre_lecture: false,
    user: component.instructorUser,
    user_id: userId,
  };
}

// buildCoachUpdateStatsPayload builds the instructor "Update Stats" request for a
// Kai Coach. The coach has no question sections, so the request component carries
// no section_stats — the backend recomputes per-unit participation averages from
// the stored student components. The same RPC and instructor flag are used.
export function buildCoachUpdateStatsPayload(
  component: ComponentPageData,
  courseId: string,
  componentId: string,
  userId: string,
): UpdateStatsRequestPayload {
  if (!component.instructorUser) {
    throw createDataError(
      'Instructor stats updates require the instructor User object from the component query.',
    );
  }

  return {
    component: { section_stats: [] },
    component_id: componentId,
    course_id: courseId,
    grade_component: false,
    is_instructor_request: true,
    only_score_pre_lecture: false,
    user: component.instructorUser,
    user_id: userId,
  };
}

export function useUpdateStats() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getIdToken, user } = useAuth();

  return useMutation<unknown, ApiError, UpdateStatsInput>({
    mutationFn: async ({ coach, component, componentId, courseId }) => {
      if (!user) {
        throw createDataError('You must be signed in to update instructor stats.');
      }

      const kuduUserId = getKuduUserId(user) ?? user.uid;

      if (!kuduUserId) {
        throw createDataError('Unable to determine the current Kudu user ID.');
      }

      const returnTo = `${location.pathname}${location.search}${location.hash}`;
      const client = createApiClient({
        baseUrl: import.meta.env.VITE_API_BASE_URL || '',
        getIdToken,
        getLegacyToken: async () => createLegacyKuduToken(user),
        onAuthError: () =>
          navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, {
            replace: true,
          }),
      });
      const payload = coach
        ? buildCoachUpdateStatsPayload(component, courseId, componentId, kuduUserId)
        : buildUpdateStatsPayload(component, courseId, componentId, kuduUserId);

      try {
        return await client.invokeKuduMethod<
          unknown | LegacyKuduResponseEnvelope<unknown>,
          UpdateStatsRequestPayload
        >('AddOrUpdateCourseStudentComponentV2', payload);
      } catch (error) {
        throw statsFailure(error, 'The stats were not updated.');
      }
    },
    onSuccess: async (_data, variables) => {
      // Every student's scores may have changed: the gradebook must not serve
      // its cached grades (kept for up to a minute).
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['component', variables.courseId, variables.componentId],
        }),
        queryClient.invalidateQueries({
          queryKey: studentGradesQueryKeyPrefix(variables.courseId),
        }),
      ]);
    },
  });
}
